import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Chip,
  FormControl,
  Select,
  useTheme,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import {
  DatePicker,
  TimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import LockIcon from "@mui/icons-material/Lock";
import InfoIcon from "@mui/icons-material/Info";
import AppDataGrid from "../../../common/AppDataGrid";

// Hooks
import useCourses from "../../../../hooks/useCourses";
import useCoursePlanningLogger from "../../../../hooks/useCoursePlanningLogger";
import useTermData from "../../../CcpAdmin/CcpAdminTerm/hooks/useTermData";
import useHsTermData from "../../../CcpAdmin/CcpAdminHsTerms/hooks/useHsTermData";
import { formatDate } from "../../../../utils/formatDate";

// Context and Auth
import { SchoolContext } from "../../../../config/SchoolContext";
import { useAuth } from "../../../../config/AuthContext";

// Firebase
import { db } from "../../../../config/firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  getDoc,
} from "firebase/firestore";

// Components
import SearchableCourseSelect from "../../../common/SearchableCourseSelect";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const CoursePlanningForm = ({ onSubmit, onCancel, isFormLocked = false }) => {
  const theme = useTheme();
  // SchoolContext only ever exposes schoolId (it has no schoolName) — the
  // actual school name for a save is resolved via getSchoolName(schoolId)
  // below, which looks it up from useSchoolDropdown data.
  const { schoolId } = useContext(SchoolContext);
  const { currentUser } = useAuth();
  const {
    courses,
    loading: coursesLoading,
    error: coursesError,
  } = useCourses();

  // Get terms data from CcpTerm collection
  const { data: ccpTerms, loading: termsLoading } = useTermData();
  // Get HS-specific terms from CcpHsTerms collection
  const { data: ccpHsTerms, loading: hsTermsLoading } = useHsTermData();

  // Use the course planning logger hook
  const { logAddition, logBulkUpdate, logDeletion, logEvent, getSchoolName } =
    useCoursePlanningLogger();

  // Initial row structure
  const createEmptyRow = () => ({
    id: Date.now() + Math.random(),
    courseName: "",
    startDate: null,
    endDate: null,
    instructorType: "",
    // HS Faculty fields
    hsInstructorName: "",
    hsComments: "",
    // Courses with Sinclair fields
    proctorName: "",
    term: "",
    // Assigned HS term (read-only display based on selected endDate)
    assignedHsTerm: "",
    modality: "",
    days: "",
    startTime: "",
    endTime: "",
    // Track field changes for admin logging
    fieldChanges: {}, // Object to track what fields changed and their original vs new values
    isEditingExisting: false, // Flag to indicate if this is editing an existing course
    // Track if this row has been saved
    isSaved: false,
    // Track original values for saved rows to detect changes
    originalValues: null,
    // Track if this row has pending changes
    hasChanges: false,
    // Course status for cancellation workflow
    status: "", // Can be "", "Course Cancel Requested", "Course Canceled Complete"
    // Per-row term validation error
    termError: null,
  });

  const [rows, setRows] = useState([createEmptyRow()]);
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    rowToDelete: null,
  });
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    rowToCancel: null,
  });
  const [formLockDialog, setFormLockDialog] = useState({
    open: false,
  });
  const [editingRows, setEditingRows] = useState(new Set());
  const [showEditMessage, setShowEditMessage] = useState(false);
  const [termDialog, setTermDialog] = useState({ open: false, rowId: null });

  // Determine which term set to show in the dialog (HS vs Sinclair)
  const dialogRow = rows.find((r) => r.id === termDialog.rowId);
  const isHsDialog = dialogRow?.instructorType === "HS Faculty";
  const termList = isHsDialog ? ccpHsTerms : ccpTerms;
  const dialogLoading = isHsDialog ? hsTermsLoading : termsLoading;

  const modalityOptions = ["Face 2 Face", "Online"];

  const instructorTypeOptions = ["HS Faculty", "Courses W Sinclair"];

  // Helper function to get selected term data from CcpTerm collection
  const getSelectedTermData = (termName) => {
    return ccpTerms?.find((term) => term.TermName === termName);
  };

  // Helper function to determine field visibility based on instructor type, modality, and form state
  const getFieldVisibility = (instructorType, modality, isSaved = true) => {
    const baseFields = {
      courseName: true,
      instructorType: true,
    };

    if (instructorType === "HS Faculty") {
      return {
        ...baseFields,
        term: false,
        startDate: true,
        endDate: true,
        modality: false,
        days: false,
        startTime: false, // Hide start time for HS Faculty
        endTime: false, // Hide end time for HS Faculty
        proctorName: false,
        hsInstructorName: true,
        hsComments: true,
        // Ensure startDate and endDate are always editable for HS Faculty
        startDateDisabled: false,
        endDateDisabled: false,
      };
    } else if (instructorType === "Courses W Sinclair") {
      const baseFieldsWithSinclair = {
        ...baseFields,
        term: true,
        startDate: true,
        endDate: true,
        modality: true,
        hsInstructorName: false,
        hsComments: true,
      };

      if (modality === "Face 2 Face") {
        return {
          ...baseFieldsWithSinclair,
          days: true,
          startTime: true,
          endTime: true,
          proctorName: true,
        };
      } else if (modality === "Online") {
        return {
          ...baseFieldsWithSinclair,
          days: false,
          startTime: false,
          endTime: false,
          proctorName: true,
        };
      } else {
        // No modality selected yet
        return {
          ...baseFieldsWithSinclair,
          days: false,
          startTime: false,
          endTime: false,
          proctorName: false,
        };
      }
    }

    // Default - show only base fields
    return {
      ...baseFields,
      term: false,
      startDate: false,
      endDate: false,
      modality: false,
      days: false,
      startTime: false,
      endTime: false,
      proctorName: false,
      hsInstructorName: false,
      hsComments: false,
    };
  };

  // Check if row is empty
  const isRowEmpty = (row) => {
    return (
      !row.courseName &&
      !row.startDate &&
      !row.endDate &&
      !row.instructorType &&
      !row.hsInstructorName &&
      !row.hsComments &&
      !row.proctorName &&
      !row.term &&
      !row.modality &&
      !row.days &&
      !row.startTime &&
      !row.endTime &&
      !row.changeAction &&
      !row.specificChange
    );
  };

  // Helper to check if a date is between two dates (inclusive)
  const isDateInRange = (date, start, end) => {
    if (!date || !start || !end) return false;
    return (
      date.isSame(start, "day") ||
      date.isSame(end, "day") ||
      (date.isAfter(start, "day") && date.isBefore(end, "day"))
    );
  };

  const handleRowChange = (rowId, field, value) => {
    setEditingRows((prev) => new Set(prev).add(rowId));
    setShowEditMessage(true);

    setRows((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          const updatedRow = { ...row, [field]: value };

          // Special handling for instructor type change
          if (field === "instructorType") {
            // Clear fields that become irrelevant when instructor type changes
            updatedRow.term = "";
            updatedRow.startDate = null;
            updatedRow.endDate = null;
            updatedRow.modality = "";
            updatedRow.days = "";
            updatedRow.startTime = "";
            updatedRow.endTime = "";
            updatedRow.proctorName = "";
            updatedRow.hsComments = "";
            // clear any previous term error when instructor type changes
            updatedRow.termError = null;
            updatedRow.assignedHsTerm = "";
          }

          // Special handling for term change - auto-populate dates from CcpTerm collection
          if (
            field === "term" &&
            updatedRow.instructorType === "Courses W Sinclair"
          ) {
            if (value) {
              // Term selected - auto-populate dates
              const termData = getSelectedTermData(value);
              if (termData && termData.StartDate && termData.EndDate) {
                try {
                  // Safely set dayjs dates from term data
                  updatedRow.startDate = termData.StartDate
                    ? dayjs(termData.StartDate)
                    : null;
                  updatedRow.endDate = termData.EndDate
                    ? dayjs(termData.EndDate)
                    : null;
                  updatedRow.termError = null;
                } catch (error) {
                  console.error("Error applying term dates:", error);
                  updatedRow.startDate = null;
                  updatedRow.endDate = null;
                  updatedRow.termError = null;
                }
              } else {
                // Clear dates if term data is incomplete
                updatedRow.startDate = null;
                updatedRow.endDate = null;
              }
            } else {
              // Term cleared - clear dates as well
              updatedRow.startDate = null;
              updatedRow.endDate = null;
            }
          }

          // Special logic for HS Faculty: auto-populate term based on entered dates
          if (
            updatedRow.instructorType === "HS Faculty" &&
            field === "endDate" &&
            updatedRow.endDate &&
            ccpHsTerms?.length
          ) {
            // Assign HS term based only on the selected endDate.
            // Find the first HS term whose EndDate is on-or-after the selected endDate.
            const sortedHsTerms = [...ccpHsTerms].sort((a, b) =>
              dayjs(a.EndDate).isBefore(dayjs(b.EndDate)) ? -1 : 1,
            );
            const matchingTerm = sortedHsTerms.find((term) =>
              dayjs(term.EndDate).isSameOrAfter(updatedRow.endDate, "day"),
            );
            // Store assigned HS term separately for display and also set the term value
            updatedRow.assignedHsTerm = matchingTerm
              ? matchingTerm.TermName
              : "";
            updatedRow.term = matchingTerm ? matchingTerm.TermName : "";
            // Do not open a modal for HS Faculty; clear any previous inline term error here.
            updatedRow.termError = matchingTerm ? null : null;
            if (matchingTerm) {
              console.log(
                "Assigned HS term based on endDate:",
                matchingTerm.TermName,
              );
            } else {
              console.warn(
                "No HS term found for selected endDate:",
                updatedRow.endDate?.toString(),
              );
            }
          }

          // Special handling for modality change
          if (field === "modality") {
            // Clear fields that become irrelevant when modality changes
            updatedRow.days = "";
            updatedRow.startTime = "";
            updatedRow.endTime = "";
            updatedRow.proctorName = "";
          }

          // For saved rows, track if there are changes from original values
          if (row.isSaved && row.originalValues) {
            // Helper function to compare values (handles dayjs objects and other edge cases)
            const valuesAreEqual = (val1, val2) => {
              // Handle null/undefined cases
              if (val1 === null && val2 === null) return true;
              if (val1 === undefined && val2 === undefined) return true;
              if (val1 === null && val2 === undefined) return true;
              if (val1 === undefined && val2 === null) return true;
              if (
                (val1 === null || val1 === undefined) &&
                (val2 === null || val2 === undefined)
              )
                return true;
              if (
                (val1 === null || val1 === undefined) !==
                (val2 === null || val2 === undefined)
              )
                return false;

              // Handle dayjs objects
              if (val1 && val1.$isDayjsObject && val2 && val2.$isDayjsObject) {
                return val1.isSame(val2);
              }
              // Handle case where one is dayjs and other is null/undefined
              if (val1 && val1.$isDayjsObject) {
                return val2 && val2.$isDayjsObject && val1.isSame(val2);
              }
              if (val2 && val2.$isDayjsObject) {
                return val1 && val1.$isDayjsObject && val2.isSame(val1);
              }

              // Handle regular values
              return val1 === val2;
            };

            // Check if any field has changed from original
            const hasAnyChanges = Object.keys(row.originalValues).some(
              (key) => {
                const currentValue = key === field ? value : updatedRow[key];
                const originalValue = row.originalValues[key];
                const areEqual = valuesAreEqual(originalValue, currentValue);

                return !areEqual;
              },
            );
            updatedRow.hasChanges = hasAnyChanges;

            // Track specific field changes for admin area (when editing existing courses)
            if (
              row.isEditingExisting &&
              row.originalValuesForEdit &&
              row.originalValuesForEdit[field] !== undefined
            ) {
              const originalValue = row.originalValuesForEdit[field];
              const currentValue = value;

              // Skip tracking courseName as it's not editable
              if (field !== "courseName") {
                if (!valuesAreEqual(originalValue, currentValue)) {
                  // Field has changed - track it
                  // Serialize values for Firestore compatibility
                  const serializeValue = (val) => {
                    if (val === null || val === undefined) return null;
                    if (val && val.$isDayjsObject) return val.toISOString();
                    // For objects/arrays, try JSON stringify, otherwise return as-is
                    if (typeof val === "object") {
                      try {
                        return JSON.stringify(val);
                      } catch (e) {
                        return String(val);
                      }
                    }
                    return val;
                  };

                  updatedRow.fieldChanges = {
                    ...row.fieldChanges,
                    [field]: {
                      originalValue: serializeValue(originalValue),
                      newValue: serializeValue(currentValue),
                      fieldName: field,
                      timestamp: new Date().toISOString(),
                    },
                  };
                } else {
                  // Field was changed back to original - remove from tracking
                  const newFieldChanges = { ...row.fieldChanges };
                  delete newFieldChanges[field];
                  updatedRow.fieldChanges = newFieldChanges;
                }
              }
            }
          }

          return updatedRow;
        }
        return row;
      }),
    );
  };

  // Load existing course planning records for this school
  const loadExistingRecords = async () => {
    if (!schoolId) return;

    try {
      setLoadingExisting(true);
      const q = query(
        collection(db, "CoursePlanningForm"),
        where("schoolId", "==", schoolId),
        orderBy("createdAt", "desc"),
      );

      const querySnapshot = await getDocs(q);
      const existingRecords = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const recordData = {
          id: doc.id, // Use Firestore document ID
          courseName: data.courseName || "",
          startDate: data.startDate ? dayjs(data.startDate) : null,
          endDate: data.endDate ? dayjs(data.endDate) : null,
          instructorType: data.instructorType || "",
          hsInstructorName: data.hsInstructorName || "",
          hsComments: data.hsComments || "",
          proctorName: data.proctorName || "",
          term: data.term || "",
          // Persist assigned HS term for display (fallback to term if present)
          assignedHsTerm: data.assignedHsTerm || data.term || "",
          modality: data.modality || "",
          days: data.days || "",
          startTime: data.startTime || "",
          endTime: data.endTime || "",
          status: data.status || "", // Load cancellation status
          isSaved: true, // Mark existing records as saved
          hasChanges: false, // No changes initially
        };

        // Store original values for change tracking
        recordData.originalValues = {
          courseName: recordData.courseName,
          startDate: recordData.startDate,
          endDate: recordData.endDate,
          instructorType: recordData.instructorType,
          hsInstructorName: recordData.hsInstructorName,
          hsComments: recordData.hsComments,
          proctorName: recordData.proctorName,
          term: recordData.term,
          modality: recordData.modality,
          days: recordData.days,
          startTime: recordData.startTime,
          endTime: recordData.endTime,
        };

        existingRecords.push(recordData);
      });

      // Set rows with existing records only (don't add empty row automatically)
      if (existingRecords.length > 0) {
        setRows(existingRecords); // Only existing records, no empty row
      } else {
        setRows([]); // Empty array if no existing records
      }
    } catch (error) {
      console.error("Error loading existing records:", error);
      setSnackbar({
        open: true,
        message: "Error loading existing records",
        severity: "error",
      });
    } finally {
      setLoadingExisting(false);
    }
  };

  // Load existing records when component mounts or schoolId changes
  useEffect(() => {
    loadExistingRecords();
  }, [schoolId]);

  const addRow = () => {
    const newRow = createEmptyRow();

    setRows((prev) => [newRow, ...prev]); // Add new row at the top
  };

  const deleteRow = (rowId) => {
    const rowToDelete = rows.find((row) => row.id === rowId);

    if (rowToDelete?.isSaved) {
      // For saved rows, show confirmation dialog before deleting
      setDeleteDialog({
        open: true,
        rowToDelete: rowToDelete,
      });
    } else {
      // For unsaved rows, just remove from state (no logging needed)
      setRows((prev) => prev.filter((row) => row.id !== rowId));
    }
  };

  const handleDeleteConfirm = async () => {
    const { rowToDelete } = deleteDialog;

    if (rowToDelete) {
      try {
        // Log the deletion
        await logDeletion(rowToDelete);

        // Delete from Firestore if it's a saved row with a valid document ID
        if (
          rowToDelete.isSaved &&
          rowToDelete.id &&
          typeof rowToDelete.id === "string"
        ) {
          const docRef = doc(db, "CoursePlanningForm", rowToDelete.id);
          await deleteDoc(docRef);
        }

        // Remove from local state
        setRows((prev) => prev.filter((row) => row.id !== rowToDelete.id));
        setSnackbar({
          open: true,
          message: "Course planning entry deleted successfully",
          severity: "success",
        });
      } catch (error) {
        console.error("Error deleting course planning entry:", error);
        setSnackbar({
          open: true,
          message: "Error deleting entry. Please try again.",
          severity: "error",
        });
      }
    }

    // Close the dialog
    setDeleteDialog({
      open: false,
      rowToDelete: null,
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      rowToDelete: null,
    });
  };

  // Cancel Course functionality (for locked forms)
  const cancelCourse = (rowId) => {
    const rowToCancel = rows.find((row) => row.id === rowId);

    if (rowToCancel?.isSaved) {
      // For saved rows, show confirmation dialog before canceling
      setCancelDialog({
        open: true,
        rowToCancel: rowToCancel,
      });
    }
  };

  const handleCancelConfirm = async () => {
    const { rowToCancel } = cancelDialog;

    if (rowToCancel) {
      try {
        // Update the course status to "Course Cancel Requested"
        if (
          rowToCancel.isSaved &&
          rowToCancel.id &&
          typeof rowToCancel.id === "string"
        ) {
          const docRef = doc(db, "CoursePlanningForm", rowToCancel.id);
          await updateDoc(docRef, {
            status: "Course Cancel Requested",
            cancelRequestedAt: new Date().toISOString(),
            cancelRequestedBy: currentUser?.uid,
          });

          // Log the cancel request
          await logEvent(
            "Cancel Request",
            `Course "${rowToCancel.courseName}" has been requested for cancellation by the school.`,
            {
              ...rowToCancel,
              type: "Cancel Request",
              status: "Course Cancel Requested",
              coursePlanningFormId: rowToCancel.id,
            },
            null,
            schoolId,
          );
        }

        // Update local state to reflect the cancel request
        setRows((prev) =>
          prev.map((row) =>
            row.id === rowToCancel.id
              ? {
                  ...row,
                  status: "Course Cancel Requested",
                  cancelRequestedAt: new Date().toISOString(),
                  cancelRequestedBy: currentUser?.uid,
                }
              : row,
          ),
        );

        setSnackbar({
          open: true,
          message: "Course cancellation request submitted successfully",
          severity: "success",
        });
      } catch (error) {
        console.error("Error requesting course cancellation:", error);
        setSnackbar({
          open: true,
          message: "Error submitting cancellation request. Please try again.",
          severity: "error",
        });
      }
    }

    // Close the dialog
    setCancelDialog({
      open: false,
      rowToCancel: null,
    });
  };

  const handleCancelCancel = () => {
    setCancelDialog({
      open: false,
      rowToCancel: null,
    });
  };

  // Every visible field is required except HS Comments and Proctor Name,
  // which stay optional regardless of instructor type/modality.
  const validateRow = (row) => {
    const errors = [];
    const fieldVisibility = getFieldVisibility(
      row.instructorType,
      row.modality,
      row.isSaved,
    );

    if (!row.courseName) errors.push("Course Name is required");
    if (!row.instructorType) errors.push("Instructor Type is required");

    if (row.instructorType === "HS Faculty") {
      if (!row.startDate) errors.push("Start Date is required");
      if (!row.endDate) errors.push("End Date is required");
      if (fieldVisibility.hsInstructorName && !row.hsInstructorName) {
        errors.push("HS Instructor Name is required");
      }
    } else if (row.instructorType === "Courses W Sinclair") {
      if (fieldVisibility.term && !row.term) {
        errors.push("Term is required");
      }
      if (fieldVisibility.modality && !row.modality) {
        errors.push("Modality is required");
      }
      if (fieldVisibility.startDate && !row.startDate) {
        errors.push("Start Date is required");
      }
      if (fieldVisibility.endDate && !row.endDate) {
        errors.push("End Date is required");
      }
      if (fieldVisibility.days && !row.days) {
        errors.push("Days is required");
      }
      if (fieldVisibility.startTime && !row.startTime) {
        errors.push("Start Time is required");
      }
      if (fieldVisibility.endTime && !row.endTime) {
        errors.push("End Time is required");
      }
    }

    return errors;
  };

  const handleEditCourse = (row) => {
    // Enable editing mode for existing course
    setRows((prevRows) =>
      prevRows.map((r) =>
        r.id === row.id
          ? {
              ...r,
              isEditingExisting: true,
              originalValuesForEdit: {
                // Store original values for reverting if user cancels edit
                instructorType: r.instructorType,
                hsInstructorName: r.hsInstructorName,
                hsComments: r.hsComments,
                proctorName: r.proctorName,
                term: r.term,
                modality: r.modality,
                days: r.days,
                startTime: r.startTime,
                endTime: r.endTime,
                startDate: r.startDate,
                endDate: r.endDate,
              },
              fieldChanges: {}, // Reset field changes
            }
          : r,
      ),
    );

    // Mark as editing
    setEditingRows((prev) => new Set(prev).add(row.id));
    setShowEditMessage(true);
  };

  const handleCancelEdit = (row) => {
    // Revert all changes and exit edit mode
    setRows((prevRows) =>
      prevRows.map((r) =>
        r.id === row.id && r.originalValuesForEdit
          ? {
              ...r,
              // Restore all original values
              instructorType: r.originalValuesForEdit.instructorType,
              hsInstructorName: r.originalValuesForEdit.hsInstructorName,
              hsComments: r.originalValuesForEdit.hsComments,
              proctorName: r.originalValuesForEdit.proctorName,
              term: r.originalValuesForEdit.term,
              modality: r.originalValuesForEdit.modality,
              days: r.originalValuesForEdit.days,
              startTime: r.originalValuesForEdit.startTime,
              endTime: r.originalValuesForEdit.endTime,
              startDate: r.originalValuesForEdit.startDate,
              endDate: r.originalValuesForEdit.endDate,
              // Exit edit mode
              isEditingExisting: false,
              originalValuesForEdit: undefined,
              fieldChanges: {},
              hasChanges: false,
            }
          : r,
      ),
    );

    // Remove from editing state
    setEditingRows((prev) => {
      const newSet = new Set(prev);
      newSet.delete(row.id);
      return newSet;
    });

    // Hide edit message if no other rows are being edited
    setEditingRows((current) => {
      if (current.size === 0) {
        setShowEditMessage(false);
      }
      return current;
    });
  };

  const handleSaveRow = async (rowToSave) => {
    try {
      setLoading(true);

      // Require HS Faculty start+end to fall inside a single CcpTerm before saving.
      // If the term was already auto-assigned during date selection, trust it and skip
      // re-validation to avoid false negatives (e.g. start date before term start).
      let updatedRowToSave = { ...rowToSave };
      if (
        updatedRowToSave.instructorType === "HS Faculty" &&
        updatedRowToSave.startDate &&
        updatedRowToSave.endDate &&
        ccpHsTerms &&
        ccpHsTerms.length > 0 &&
        !updatedRowToSave.term // only validate if term wasn't already auto-assigned
      ) {
        const sortedHsTerms = [...ccpHsTerms].sort((a, b) =>
          dayjs(a.EndDate).isBefore(dayjs(b.EndDate)) ? -1 : 1,
        );
        const matchingTerm = sortedHsTerms.find((term) =>
          dayjs(term.EndDate).isSameOrAfter(updatedRowToSave.endDate, "day"),
        );

        if (matchingTerm) {
          updatedRowToSave.term = matchingTerm.TermName;
          updatedRowToSave.termError = null;
        } else {
          setSnackbar({
            open: true,
            message:
              "Selected end date does not fall inside any HS term. Please adjust the end date.",
            severity: "error",
          });
          setLoading(false);
          return;
        }
      }

      // Debug: log the row being saved
      console.log("[DEBUG] handleSaveRow - Saving row:", updatedRowToSave);

      if (isFormLocked) {
        // When form is locked, handle new course additions and existing course edits

        if (!rowToSave.isSaved) {
          // New course addition - save directly to Firestore (even when locked)
          const errors = validateRow(rowToSave);
          if (errors.length > 0) {
            setSnackbar({
              open: true,
              message: `Validation errors: ${errors.join(", ")}`,
              severity: "error",
            });
            return;
          }

          // Save new course directly to Firestore
          console.log(
            "[DEBUG] Saving new course (locked). schoolId:",
            schoolId,
            "row:",
            updatedRowToSave,
          );
          const resolvedSchoolName = getSchoolName(schoolId);
          console.log(
            "[DEBUG] resolvedSchoolName for save (locked):",
            resolvedSchoolName,
          );
          const formData = {
            courseName: updatedRowToSave.courseName,
            startDate: updatedRowToSave.startDate
              ? updatedRowToSave.startDate.toISOString()
              : null,
            endDate: updatedRowToSave.endDate
              ? updatedRowToSave.endDate.toISOString()
              : null,
            instructorType: updatedRowToSave.instructorType,
            hsInstructorName: updatedRowToSave.hsInstructorName,
            hsComments: updatedRowToSave.hsComments,
            proctorName: updatedRowToSave.proctorName,
            term: updatedRowToSave.term,
            modality: updatedRowToSave.modality,
            days: updatedRowToSave.days,
            startTime: updatedRowToSave.startTime,
            endTime: updatedRowToSave.endTime,
            changeAction: isFormLocked ? "Addition" : "",
            specificChange: isFormLocked ? "New course addition" : "",
            schoolId: schoolId,
            schoolName: resolvedSchoolName,
            createdBy: currentUser?.uid,
            createdAt: new Date().toISOString(),
          };

          const docRef = await addDoc(
            collection(db, "CoursePlanningForm"),
            formData,
          );

          // Log the addition
          await logAddition(rowToSave);

          setSnackbar({
            open: true,
            message: "Course addition saved successfully",
            severity: "success",
          });

          // Update the row state to mark it as saved
          setRows((prevRows) => {
            return prevRows.map((row) => {
              if (row.id === rowToSave.id) {
                return {
                  ...row,
                  id: docRef.id, // Use the Firestore document ID
                  isSaved: true,
                  hasChanges: false,
                  isPending: false, // Not pending - it's a completed addition
                  originalValues: {
                    courseName: row.courseName,
                    startDate: row.startDate,
                    endDate: row.endDate,
                    instructorType: row.instructorType,
                    hsInstructorName: row.hsInstructorName,
                    hsComments: row.hsComments,
                    proctorName: row.proctorName,
                    term: row.term,
                    modality: row.modality,
                    days: row.days,
                    startTime: row.startTime,
                    endTime: row.endTime,
                    changeAction: row.changeAction,
                    specificChange: row.specificChange,
                  },
                };
              }
              return row;
            });
          });

          return;
        } else if (
          rowToSave.isEditingExisting &&
          Object.keys(rowToSave.fieldChanges).length > 0
        ) {
          // Existing course edit - save changes directly to the course record
          const errors = validateRow(rowToSave);
          if (errors.length > 0) {
            setSnackbar({
              open: true,
              message: `Validation errors: ${errors.join(", ")}`,
              severity: "error",
            });
            return;
          }

          // Save the updated course data directly to Firestore
          console.log(
            "[DEBUG] Updating existing course (locked). schoolId:",
            schoolId,
            "row:",
            updatedRowToSave,
          );
          const resolvedSchoolName = getSchoolName(schoolId);
          console.log(
            "[DEBUG] resolvedSchoolName for update (locked):",
            resolvedSchoolName,
          );
          const docRef = doc(db, "CoursePlanningForm", rowToSave.id);
          const updateData = {
            courseName: updatedRowToSave.courseName,
            startDate: updatedRowToSave.startDate
              ? updatedRowToSave.startDate.toISOString()
              : null,
            endDate: updatedRowToSave.endDate
              ? updatedRowToSave.endDate.toISOString()
              : null,
            instructorType: updatedRowToSave.instructorType,
            hsInstructorName: updatedRowToSave.hsInstructorName,
            hsComments: updatedRowToSave.hsComments,
            proctorName: updatedRowToSave.proctorName,
            term: updatedRowToSave.term,
            modality: updatedRowToSave.modality,
            days: updatedRowToSave.days,
            startTime: updatedRowToSave.startTime,
            endTime: updatedRowToSave.endTime,
            updatedAt: new Date().toISOString(),
            updatedBy: currentUser?.uid,
            schoolName: resolvedSchoolName,
          };

          await updateDoc(docRef, updateData);

          // Create a detailed log of what changed
          const changedFields = [];
          await Promise.all(
            Object.keys(rowToSave.fieldChanges).map(async (fieldName) => {
              const change = rowToSave.fieldChanges[fieldName];
              const formatValue = (value) => {
                if (!value) return "Not set";
                if (typeof value === "string" && value.includes("T")) {
                  // ISO date string
                  return formatDate(value);
                }
                return value;
              };

              changedFields.push(
                `${fieldName}: ${formatValue(change.originalValue)} → ${formatValue(change.newValue)}`,
              );

              // Log the changes for admin visibility
              await logEvent(
                "Course Updated",
                `Course "${rowToSave.courseName}" was updated. Changes: ${changedFields.join(", ")}`,
                rowToSave,
                rowToSave.originalValuesForEdit,
                schoolId,
              );
            }),
          );

          setSnackbar({
            open: true,
            message: "Course changes saved successfully",
            severity: "success",
          });

          // Update row state - clear editing mode and field changes
          setRows((prevRows) => {
            return prevRows.map((row) => {
              if (row.id === rowToSave.id) {
                const updatedRow = {
                  ...row,
                  isEditingExisting: false,
                  hasChanges: false,
                  isPending: false,
                  fieldChanges: {},
                  originalValuesForEdit: undefined,
                };

                // Update original values to reflect the new saved state
                updatedRow.originalValues = {
                  courseName: updatedRow.courseName,
                  startDate: updatedRow.startDate,
                  endDate: updatedRow.endDate,
                  instructorType: updatedRow.instructorType,
                  hsInstructorName: updatedRow.hsInstructorName,
                  hsComments: updatedRow.hsComments,
                  proctorName: updatedRow.proctorName,
                  term: updatedRow.term,
                  modality: updatedRow.modality,
                  days: updatedRow.days,
                  startTime: updatedRow.startTime,
                  endTime: updatedRow.endTime,
                  changeAction: updatedRow.changeAction,
                  specificChange: updatedRow.specificChange,
                };

                return updatedRow;
              }
              return row;
            });
          });

          return;
        } else {
          setSnackbar({
            open: true,
            message: "No changes to save",
            severity: "info",
          });
          return;
        }
      }

      // Normal behavior when form is not locked
      // Validate the specific row
      const errors = validateRow(rowToSave);

      if (errors.length > 0) {
        const actualRowIndex = rows.indexOf(rowToSave) + 1;
        setSnackbar({
          open: true,
          message: `Row ${actualRowIndex}: ${errors.join(", ")}`,
          severity: "error",
        });
        return;
      }

      if (rowToSave.isSaved) {
        // Update existing row
        if (rowToSave.id && typeof rowToSave.id === "string") {
          console.log(
            "[DEBUG] Updating existing course (unlocked). schoolId:",
            schoolId,
            "row:",
            rowToSave,
          );
          const resolvedSchoolName = getSchoolName(schoolId);
          console.log(
            "[DEBUG] resolvedSchoolName for update (unlocked):",
            resolvedSchoolName,
          );
          const docRef = doc(db, "CoursePlanningForm", rowToSave.id);
          const updateData = {
            courseName: rowToSave.courseName,
            startDate: rowToSave.startDate
              ? rowToSave.startDate.toISOString()
              : null,
            endDate: rowToSave.endDate ? rowToSave.endDate.toISOString() : null,
            instructorType: rowToSave.instructorType,
            hsInstructorName: rowToSave.hsInstructorName,
            hsComments: rowToSave.hsComments,
            proctorName: rowToSave.proctorName,
            term: rowToSave.term,
            modality: rowToSave.modality,
            days: rowToSave.days,
            startTime: rowToSave.startTime,
            endTime: rowToSave.endTime,
            updatedAt: new Date().toISOString(),
            updatedBy: currentUser?.uid,
            schoolName: resolvedSchoolName,
          };
          await updateDoc(docRef, updateData);

          // Log the update
          if (rowToSave.originalValues) {
            // Helper function to compare values (handles dayjs objects and other edge cases)
            const valuesAreEqual = (val1, val2) => {
              // Handle null/undefined cases
              if (val1 === null && val2 === null) return true;
              if (val1 === undefined && val2 === undefined) return true;
              if (val1 === null && val2 === undefined) return true;
              if (val1 === undefined && val2 === null) return true;
              if (
                (val1 === null || val1 === undefined) &&
                (val2 === null || val2 === undefined)
              )
                return true;
              if (
                (val1 === null || val1 === undefined) !==
                (val2 === null || val2 === undefined)
              )
                return false;

              // Handle dayjs objects
              if (val1 && val1.$isDayjsObject && val2 && val2.$isDayjsObject) {
                return val1.isSame(val2);
              }
              // Handle case where one is dayjs and other is null/undefined
              if (val1 && val1.$isDayjsObject) {
                return val2 && val2.$isDayjsObject && val1.isSame(val2);
              }
              if (val2 && val2.$isDayjsObject) {
                return val1 && val1.$isDayjsObject && val2.isSame(val1);
              }

              // Handle regular values
              return val1 === val2;
            };

            // Helper function to format values for display in logs
            const formatValueForLog = (value) => {
              if (value === null || value === undefined) return "empty";
              if (value && value.$isDayjsObject) return formatDate(value);
              if (value === "") return "empty";
              return String(value);
            };

            // Create changed fields array with proper structure
            const changedFields = [];
            Object.keys(rowToSave.originalValues).forEach((field) => {
              const originalValue = rowToSave.originalValues[field];
              const currentValue = rowToSave[field];

              if (!valuesAreEqual(originalValue, currentValue)) {
                changedFields.push({
                  field,
                  oldValue: formatValueForLog(originalValue),
                  newValue: formatValueForLog(currentValue),
                });
              }
            });

            if (changedFields.length > 0) {
              await logBulkUpdate(
                changedFields,
                rowToSave,
                rowToSave.originalValues,
              );
            }
          }
        }
      } else {
        // Save new row
        console.log(
          "[DEBUG] Saving new course (unlocked). schoolId:",
          schoolId,
          "row:",
          rowToSave,
        );
        const resolvedSchoolName = getSchoolName(schoolId);
        console.log(
          "[DEBUG] resolvedSchoolName for save (unlocked):",
          resolvedSchoolName,
        );
        const formData = {
          courseName: rowToSave.courseName,
          startDate: rowToSave.startDate
            ? rowToSave.startDate.toISOString()
            : null,
          endDate: rowToSave.endDate ? rowToSave.endDate.toISOString() : null,
          instructorType: rowToSave.instructorType,
          hsInstructorName: rowToSave.hsInstructorName,
          hsComments: rowToSave.hsComments,
          proctorName: rowToSave.proctorName,
          term: rowToSave.term,
          modality: rowToSave.modality,
          days: rowToSave.days,
          startTime: rowToSave.startTime,
          endTime: rowToSave.endTime,
          schoolId: schoolId,
          schoolName: resolvedSchoolName,
          createdBy: currentUser?.uid,
          createdAt: new Date().toISOString(),
        };

        await addDoc(collection(db, "CoursePlanningForm"), formData);

        // Log the addition
        await logAddition(rowToSave);
      }

      // Update the row state
      setRows((prevRows) => {
        return prevRows.map((row) => {
          if (row.id === rowToSave.id) {
            const updatedRow = { ...row, hasChanges: false };
            if (!row.isSaved) {
              updatedRow.isSaved = true;
            }

            // Update originalValues with current values (after save)
            updatedRow.originalValues = {
              courseName: updatedRow.courseName,
              startDate: updatedRow.startDate,
              endDate: updatedRow.endDate,
              instructorType: updatedRow.instructorType,
              hsInstructorName: updatedRow.hsInstructorName,
              hsComments: updatedRow.hsComments,
              proctorName: updatedRow.proctorName,
              term: updatedRow.term,
              modality: updatedRow.modality,
              days: updatedRow.days,
              startTime: updatedRow.startTime,
              endTime: updatedRow.endTime,
              changeAction: updatedRow.changeAction,
              specificChange: updatedRow.specificChange,
            };

            // If this was an edit mode, exit edit mode
            if (updatedRow.isEditingExisting) {
              updatedRow.isEditingExisting = false;
              updatedRow.originalValuesForEdit = undefined;
              updatedRow.fieldChanges = {};
            }

            return updatedRow;
          }
          return row;
        });
      });

      // Clear editing state for this row
      setEditingRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rowToSave.id);
        return newSet;
      });

      // Hide edit message if no rows are being edited
      setEditingRows((current) => {
        if (current.size === 0) {
          setShowEditMessage(false);
        }
        return current;
      });

      // Show success message
      setSnackbar({
        open: true,
        message: "Row saved successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error saving row:", error);
      setSnackbar({
        open: true,
        message: "Error saving row. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Handle field focus to show editing state
  const handleFieldFocus = (rowId) => {
    setEditingRows((prev) => new Set(prev).add(rowId));
    setShowEditMessage(true);
  };

  // Handle field blur - only remove from editing if no changes are pending
  const handleFieldBlur = (rowId) => {
    const row = rows.find((r) => r.id === rowId);
    // Don't remove editing state if there are unsaved changes
    if (row && !row.hasChanges && row.isSaved) {
      setEditingRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rowId);
        return newSet;
      });
    }
  };

  // Handle form lock dialog
  const handleOpenFormLockDialog = () => {
    setFormLockDialog({ open: true });
  };

  const handleCloseFormLockDialog = () => {
    setFormLockDialog({ open: false });
  };

  // Handler for when a term is selected from the Term Dialog
  const handleTermSelect = (term) => {
    if (!termDialog?.rowId) return;
    // Only allow manual term selection for non-HS rows. HS Faculty term selection is auto-assigned based on endDate.
    const dialogRow = rows.find((r) => r.id === termDialog.rowId);
    if (dialogRow?.instructorType === "HS Faculty") {
      // Ignore manual term selection for HS Faculty
      setTermDialog({ open: false, rowId: null });
      return;
    }
    setRows((prev) =>
      prev.map((row) =>
        row.id === termDialog.rowId
          ? {
              ...row,
              term: term.TermName,
              startDate: dayjs(term.StartDate),
              endDate: dayjs(term.EndDate),
              termError: null,
            }
          : row,
      ),
    );

    setTermDialog({ open: false, rowId: null });
    setSnackbar({
      open: true,
      message: `Term "${term.TermName}" applied.`,
      severity: "success",
    });
  };

  // Excel-style grid columns for the course list (replaces the old per-row
  // Accordion). Every renderCell reads live values off `params.row` — the
  // data itself doesn't need to be a dependency here, DataGrid re-invokes
  // renderCell with fresh row data on every render regardless of whether
  // `columns` itself is memoized. `rows.length` IS a dependency though: the
  // Delete button's disabled state ("can't delete the only row") reads the
  // total row count directly, not something DataGrid re-supplies per cell.
  // handleRowChange/handleFieldFocus/etc. are intentionally NOT listed as
  // deps — they're recreated every render but only close over ccpTerms/
  // ccpHsTerms (already listed below) for anything that matters here, so
  // memoizing on those covers correctness without rebuilding columns (and
  // losing input focus) on every keystroke.
  const courseColumns = useMemo(
    () => [
      {
        field: "courseName",
        headerName: "Course Name *",
        minWidth: 260,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          return (
            <Box sx={{ width: "100%", py: 0.5 }}>
              <SearchableCourseSelect
                courses={courses}
                value={row.courseName}
                onChange={(value) =>
                  handleRowChange(row.id, "courseName", value)
                }
                onFocus={() => handleFieldFocus(row.id)}
                onBlur={() => handleFieldBlur(row.id)}
                loading={coursesLoading}
                disabled={isFormLocked && row.isSaved}
                fullWidth
                size="small"
                placeholder="Search and select a course..."
                freeSolo={false}
                disableClearable={true}
                // Inside a DataGrid cell the dropdown must portal to
                // document.body — the cell/row clip overflow, so an
                // in-place (disablePortal) popper gets cut off after a
                // couple of options instead of floating above other rows.
                disablePortal={false}
                sx={{
                  "& .MuiInputBase-root": { height: 40 },
                  "& .MuiAutocomplete-input": {
                    padding: "8px 4px 8px 0 !important",
                  },
                }}
                slotProps={{ input: { readOnly: true } }}
              />
              {!row.courseName && !isRowEmpty(row) && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.25,
                    fontStyle: "italic",
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[400]
                        : "#666",
                  }}
                >
                  (In Progress)
                </Typography>
              )}
              {isRowEmpty(row) && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.25,
                    fontStyle: "italic",
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[500]
                        : "#999",
                  }}
                >
                  (Empty)
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: "instructorType",
        headerName: "Instructor Type *",
        minWidth: 170,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          return (
            <FormControl fullWidth size="small">
              <Select
                value={row.instructorType}
                onChange={(e) =>
                  handleRowChange(row.id, "instructorType", e.target.value)
                }
                onFocus={() => handleFieldFocus(row.id)}
                onBlur={() => handleFieldBlur(row.id)}
                onKeyDown={(e) => e.stopPropagation()}
                disabled={
                  isFormLocked && row.isSaved && !row.isEditingExisting
                }
                displayEmpty
                sx={{
                  height: 40,
                  fontSize: "0.875rem",
                  "& .MuiSelect-select": {
                    padding: "8px 32px 8px 12px",
                    backgroundColor: isFormLocked ? "#f5f5f5" : "inherit",
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select Type</em>
                </MenuItem>
                {instructorTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      },
      {
        field: "term",
        headerName: "Term *",
        minWidth: 190,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          const fieldVisibility = getFieldVisibility(
            row.instructorType,
            row.modality,
            row.isSaved,
          );
          if (!fieldVisibility.term) return null;
          return (
            <FormControl fullWidth size="small">
              <Select
                value={row.term}
                onChange={(e) =>
                  handleRowChange(row.id, "term", e.target.value)
                }
                onFocus={() => handleFieldFocus(row.id)}
                onBlur={() => handleFieldBlur(row.id)}
                onKeyDown={(e) => e.stopPropagation()}
                disabled={
                  (isFormLocked &&
                    row.isSaved &&
                    !row.isEditingExisting) ||
                  row.instructorType === "HS Faculty"
                }
                displayEmpty
                sx={{
                  height: 40,
                  fontSize: "0.875rem",
                  "& .MuiSelect-select": {
                    padding: "8px 32px 8px 12px",
                    backgroundColor: isFormLocked ? "#f5f5f5" : "inherit",
                  },
                }}
              >
                <MenuItem value="">
                  <em>
                    {row.instructorType === "HS Faculty"
                      ? "Auto from End Date"
                      : "Select Term"}
                  </em>
                </MenuItem>
                {ccpTerms?.map((term) => (
                  <MenuItem key={term.id} value={term.TermName}>
                    {term.TermName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      },
      {
        field: "modality",
        headerName: "Modality *",
        minWidth: 160,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          const fieldVisibility = getFieldVisibility(
            row.instructorType,
            row.modality,
            row.isSaved,
          );
          if (!fieldVisibility.modality) return null;
          return (
            <FormControl fullWidth size="small">
              <Select
                value={row.modality}
                onChange={(e) =>
                  handleRowChange(row.id, "modality", e.target.value)
                }
                onFocus={() => handleFieldFocus(row.id)}
                onBlur={() => handleFieldBlur(row.id)}
                onKeyDown={(e) => e.stopPropagation()}
                disabled={
                  isFormLocked && row.isSaved && !row.isEditingExisting
                }
                displayEmpty
                sx={{
                  height: 40,
                  fontSize: "0.875rem",
                  "& .MuiSelect-select": {
                    padding: "8px 32px 8px 12px",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select Modality</em>
                </MenuItem>
                {modalityOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      },
      {
        field: "assignedHsTerm",
        headerName: "Assigned Term",
        minWidth: 170,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          if (row.instructorType !== "HS Faculty") return null;
          return (
            <TextField
              value={row.assignedHsTerm || ""}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
            />
          );
        },
      },
      {
        field: "startDate",
        headerName: "Start Date *",
        minWidth: 170,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          const fieldVisibility = getFieldVisibility(
            row.instructorType,
            row.modality,
            row.isSaved,
          );
          const datesReady =
            row.instructorType !== "Courses W Sinclair" ||
            Boolean(getSelectedTermData(row.term));
          if (!fieldVisibility.startDate || !datesReady) return null;
          return (
            <DatePicker
              value={row.startDate}
              onChange={(newValue) =>
                handleRowChange(row.id, "startDate", newValue)
              }
              format="MMM DD, YYYY"
              disabled={
                row.instructorType === "Courses W Sinclair" &&
                Boolean(getSelectedTermData(row.term))
                  ? true
                  : fieldVisibility.startDateDisabled === true
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  placeholder: "Select date",
                  inputProps: { readOnly: true },
                  sx: {
                    "& .MuiInputBase-root": { height: 40, fontSize: "0.875rem" },
                  },
                },
              }}
            />
          );
        },
      },
      {
        field: "endDate",
        headerName: "End Date *",
        minWidth: 170,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          const fieldVisibility = getFieldVisibility(
            row.instructorType,
            row.modality,
            row.isSaved,
          );
          const datesReady =
            row.instructorType !== "Courses W Sinclair" ||
            Boolean(getSelectedTermData(row.term));
          if (!fieldVisibility.endDate || !datesReady) return null;
          return (
            <DatePicker
              value={row.endDate}
              onChange={(newValue) =>
                handleRowChange(row.id, "endDate", newValue)
              }
              format="MMM DD, YYYY"
              disabled={
                row.instructorType === "Courses W Sinclair" &&
                Boolean(getSelectedTermData(row.term))
                  ? true
                  : fieldVisibility.endDateDisabled === true
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  placeholder: "Select date",
                  inputProps: { readOnly: true },
                  sx: {
                    "& .MuiInputBase-root": { height: 40, fontSize: "0.875rem" },
                  },
                },
              }}
            />
          );
        },
      },
      {
        field: "days",
        headerName: "Days *",
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          const fieldVisibility = getFieldVisibility(
            row.instructorType,
            row.modality,
            row.isSaved,
          );
          if (!fieldVisibility.days) return null;
          return (
            <FormControl fullWidth size="small">
              <Select
                value={row.days}
                onChange={(e) =>
                  handleRowChange(row.id, "days", e.target.value)
                }
                onFocus={() => handleFieldFocus(row.id)}
                onBlur={() => handleFieldBlur(row.id)}
                onKeyDown={(e) => e.stopPropagation()}
                disabled={
                  isFormLocked && row.isSaved && !row.isEditingExisting
                }
                displayEmpty
                sx={{ height: 40, fontSize: "0.875rem" }}
              >
                <MenuItem value="">
                  <em>Select Days</em>
                </MenuItem>
                {[
                  "MWF",
                  "TTH",
                  "MTWTH",
                  "MTWTHF",
                  "MW",
                  "M Only",
                  "T Only",
                  "W Only",
                  "TH Only",
                  "F Only",
                ].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      },
      {
        field: "startTime",
        headerName: "Start Time *",
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          const fieldVisibility = getFieldVisibility(
            row.instructorType,
            row.modality,
            row.isSaved,
          );
          if (!fieldVisibility.startTime) return null;
          return (
            <TimePicker
              value={
                row.startTime
                  ? row.startTime.includes("M")
                    ? dayjs(row.startTime, "h:mm A")
                    : dayjs(row.startTime, "HH:mm")
                  : null
              }
              onChange={(newValue) => {
                const timeString = newValue ? newValue.format("h:mm A") : "";
                handleRowChange(row.id, "startTime", timeString);
              }}
              disabled={
                isFormLocked && row.isSaved && !row.isEditingExisting
              }
              format="h:mm A"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  onFocus: () => handleFieldFocus(row.id),
                  onBlur: () => handleFieldBlur(row.id),
                  placeholder: "Start",
                  sx: {
                    "& .MuiInputBase-root": { height: 40, fontSize: "0.875rem" },
                  },
                },
              }}
            />
          );
        },
      },
      {
        field: "endTime",
        headerName: "End Time *",
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          const fieldVisibility = getFieldVisibility(
            row.instructorType,
            row.modality,
            row.isSaved,
          );
          if (!fieldVisibility.endTime) return null;
          return (
            <TimePicker
              value={
                row.endTime
                  ? row.endTime.includes("M")
                    ? dayjs(row.endTime, "h:mm A")
                    : dayjs(row.endTime, "HH:mm")
                  : null
              }
              onChange={(newValue) => {
                const timeString = newValue ? newValue.format("h:mm A") : "";
                handleRowChange(row.id, "endTime", timeString);
              }}
              disabled={
                isFormLocked && row.isSaved && !row.isEditingExisting
              }
              format="h:mm A"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  onFocus: () => handleFieldFocus(row.id),
                  onBlur: () => handleFieldBlur(row.id),
                  placeholder: "End",
                  sx: {
                    "& .MuiInputBase-root": { height: 40, fontSize: "0.875rem" },
                  },
                },
              }}
            />
          );
        },
      },
      {
        field: "hsInstructorName",
        headerName: "HS Instructor Name *",
        minWidth: 190,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          const fieldVisibility = getFieldVisibility(
            row.instructorType,
            row.modality,
            row.isSaved,
          );
          if (!fieldVisibility.hsInstructorName) return null;
          return (
            <TextField
              value={row.hsInstructorName}
              onChange={(e) =>
                handleRowChange(row.id, "hsInstructorName", e.target.value)
              }
              onFocus={() => handleFieldFocus(row.id)}
              onBlur={() => handleFieldBlur(row.id)}
              onKeyDown={(e) => e.stopPropagation()}
              disabled={
                isFormLocked && row.isSaved && !row.isEditingExisting
              }
              fullWidth
              size="small"
              placeholder="Instructor name"
              sx={{
                "& .MuiInputBase-root": { height: 40, fontSize: "0.875rem" },
              }}
            />
          );
        },
      },
      {
        field: "proctorName",
        headerName: "Proctor Name",
        minWidth: 190,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          const fieldVisibility = getFieldVisibility(
            row.instructorType,
            row.modality,
            row.isSaved,
          );
          if (!fieldVisibility.proctorName) return null;
          return (
            <TextField
              value={row.proctorName}
              onChange={(e) =>
                handleRowChange(row.id, "proctorName", e.target.value)
              }
              onFocus={() => handleFieldFocus(row.id)}
              onBlur={() => handleFieldBlur(row.id)}
              onKeyDown={(e) => e.stopPropagation()}
              disabled={row.isSaved && !row.isEditingExisting}
              fullWidth
              size="small"
              placeholder="Proctor name"
              sx={{
                "& .MuiInputBase-root": { height: 40, fontSize: "0.875rem" },
              }}
            />
          );
        },
      },
      {
        field: "hsComments",
        headerName: "HS Comments",
        minWidth: 220,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          const fieldVisibility = getFieldVisibility(
            row.instructorType,
            row.modality,
            row.isSaved,
          );
          if (!fieldVisibility.hsComments) return null;
          return (
            <TextField
              value={row.hsComments}
              onChange={(e) =>
                handleRowChange(row.id, "hsComments", e.target.value)
              }
              onFocus={() => handleFieldFocus(row.id)}
              onBlur={() => handleFieldBlur(row.id)}
              onKeyDown={(e) => e.stopPropagation()}
              disabled={
                isFormLocked && row.isSaved && !row.isEditingExisting
              }
              fullWidth
              size="small"
              placeholder="Comments or special instructions"
              multiline
              minRows={2}
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: "0.875rem",
                  maxHeight: 64,
                  overflowY: "auto",
                },
              }}
            />
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 210,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.5 }}
            >
              {row.isPending && (
                <Chip
                  label="Change Requested"
                  size="small"
                  sx={{
                    backgroundColor: "#1976d2",
                    color: "white",
                    fontSize: "0.7rem",
                    height: 24,
                  }}
                />
              )}
              {row.changeRequestStatus === "approved" && !row.isPending && (
                <Chip
                  label="Previous Request Approved - Click to Submit New"
                  size="small"
                  sx={{
                    backgroundColor: "#4caf50",
                    color: "white",
                    fontSize: "0.7rem",
                    height: 24,
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowChange(row.id, "changeRequestStatus", "");
                    handleRowChange(row.id, "changeAction", "");
                    handleRowChange(row.id, "specificChange", "");
                    handleRowChange(row.id, "isPending", false);
                  }}
                />
              )}
              {row.changeRequestStatus === "rejected" && !row.isPending && (
                <Chip
                  label="Previous Request Rejected - Click to Submit New"
                  size="small"
                  sx={{
                    backgroundColor: "#f44336",
                    color: "white",
                    fontSize: "0.7rem",
                    height: 24,
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowChange(row.id, "changeRequestStatus", "");
                    handleRowChange(row.id, "changeAction", "");
                    handleRowChange(row.id, "specificChange", "");
                    handleRowChange(row.id, "isPending", false);
                  }}
                />
              )}
              {row.isSaved &&
                !editingRows.has(row.id) &&
                !row.isPending &&
                !row.status && (
                  <Chip
                    label="Saved"
                    size="small"
                    sx={{
                      backgroundColor: "#4caf50",
                      color: "white",
                      fontSize: "0.7rem",
                      height: 24,
                    }}
                  />
                )}
              {row.status === "Course Cancel Requested" && (
                <Chip
                  label="Course Cancel Requested"
                  size="small"
                  sx={{
                    backgroundColor: "#ff9800",
                    color: "white",
                    fontSize: "0.7rem",
                    height: 24,
                  }}
                />
              )}
              {row.status === "Course Canceled Complete" && (
                <Chip
                  label="Course Canceled Complete"
                  size="small"
                  sx={{
                    backgroundColor: "#9c27b0",
                    color: "white",
                    fontSize: "0.7rem",
                    height: 24,
                  }}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 280,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          return (
            <Box
              sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", py: 0.5 }}
              onClick={(e) => e.stopPropagation()}
            >
              {isFormLocked &&
              row.isSaved &&
              !row.isEditingExisting &&
              row.status !== "Course Cancel Requested" &&
              row.status !== "Course Canceled Complete" ? (
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={() => handleEditCourse(row)}
                  sx={{ textTransform: "none", fontSize: "0.75rem", px: 1.5 }}
                >
                  Edit Course
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={() => handleSaveRow(row)}
                  disabled={
                    isFormLocked
                      ? row.isSaved && !row.isEditingExisting
                        ? true
                        : isRowEmpty(row)
                      : isRowEmpty(row) || (!row.hasChanges && row.isSaved)
                  }
                  sx={{ textTransform: "none", fontSize: "0.75rem", px: 1.5 }}
                >
                  {isFormLocked
                    ? row.isEditingExisting
                      ? "Save Changes"
                      : "Add Course"
                    : row.isSaved
                      ? "Update"
                      : "Save"}
                </Button>
              )}

              {isFormLocked && row.isEditingExisting && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={() => handleCancelEdit(row)}
                  sx={{ textTransform: "none", fontSize: "0.75rem", px: 1.5 }}
                >
                  Cancel Edit
                </Button>
              )}

              {!isFormLocked && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => deleteRow(row.id)}
                  disabled={rows.length === 1 && !row.isSaved}
                  sx={{ textTransform: "none", fontSize: "0.75rem", px: 1.5 }}
                >
                  Delete
                </Button>
              )}

              {isFormLocked &&
                row.isSaved &&
                row.status !== "Course Cancel Requested" &&
                row.status !== "Course Canceled Complete" && (
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={<CancelIcon />}
                    onClick={() => cancelCourse(row.id)}
                    sx={{ textTransform: "none", fontSize: "0.75rem", px: 1.5 }}
                  >
                    Cancel Course
                  </Button>
                )}
            </Box>
          );
        },
      },
    ],
    [
      courses,
      coursesLoading,
      ccpTerms,
      ccpHsTerms,
      isFormLocked,
      editingRows,
      rows.length,
      theme,
    ],
  );
  if (coursesLoading || loadingExisting || termsLoading || hsTermsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        sx={{ p: 3 }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          {coursesLoading
            ? "Loading courses..."
            : termsLoading || hsTermsLoading
              ? "Loading terms..."
              : "Loading existing course planning records..."}
        </Typography>
      </Box>
    );
  }

  if (coursesError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading courses: {coursesError}</Alert>
      </Box>
    );
  }

  // Always use the main form - it handles both locked and unlocked states properly
  // The old locked view didn't support multiple change requests
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header with action buttons */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            {/* Form locked indicator */}
            {isFormLocked && (
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.warning.dark
                      : "#fff3e0",
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.warning.main}`,
                }}
              >
                <LockIcon
                  sx={{
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.warning.contrastText
                        : "#ed6c02",
                    fontSize: "1.2rem",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.warning.contrastText
                        : "#ed6c02",
                    flex: 1,
                  }}
                >
                  Form Locked
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleOpenFormLockDialog}
                  sx={{
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.warning.contrastText
                        : "#ed6c02",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(237, 108, 2, 0.1)",
                    },
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {/* Edit notification message */}
            {showEditMessage && editingRows.size > 0 && (
              <Alert
                severity="info"
                sx={{
                  mb: 2,
                  backgroundColor: "#e3f2fd",
                  "& .MuiAlert-icon": {
                    color: "#1976d2",
                  },
                }}
              >
                You are editing {editingRows.size} row
                {editingRows.size > 1 ? "s" : ""}. Don't forget to save your
                changes using the save button in each course.
              </Alert>
            )}
            {!isFormLocked && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addRow}
                >
                  Add Course
                </Button>
                {onCancel && (
                  <Button
                    variant="outlined"
                    onClick={onCancel}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                )}
                <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                  <Chip
                    label={`${rows.filter((row) => row.isSaved).length} saved`}
                    color="success"
                    size="small"
                  />
                  <Chip size="small" />
                  <Chip
                    label={`${
                      rows.filter((row) => !row.isSaved && !isRowEmpty(row))
                        .length
                    } pending`}
                    color="warning"
                    size="small"
                  />
                </Box>
              </Box>
            )}

            {!isFormLocked && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[800]
                      : theme.palette.grey[100],
                  borderRadius: 1,
                  border:
                    theme.palette.mode === "dark"
                      ? `1px solid ${theme.palette.grey[700]}`
                      : "none",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[200]
                        : theme.palette.text.secondary,
                  }}
                >
                  <strong>Status Legend:</strong> 🟢 Saved • 🔵 Editing • 🔴
                  Addition Pending • 🔷 Change Requested • ⚪ New
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Course entries - Excel-style grid */}
        {!isFormLocked && (
          <Box sx={{ width: "100%", mb: 4 }}>
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <AppDataGrid
                rows={rows}
                columns={courseColumns}
                autoHeight
                hideFooter
                disableColumnMenu
                pinnedColumns={{ left: ["courseName"], right: ["actions"] }}
                rowHeight={80}
                getRowClassName={(params) => {
                  const row = params.row;
                  if (editingRows.has(row.id)) return "cp-row-editing";
                  if (row.isPending) return "cp-row-pending";
                  if (row.isSaved) return "cp-row-saved";
                  return "cp-row-default";
                }}
                sx={{
                  "& .MuiDataGrid-cell": {
                    alignItems: "flex-start",
                    display: "flex",
                    py: 1,
                  },
                  "& .cp-row-editing": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#1e3a8a" : "#e3f2fd",
                  },
                  "& .cp-row-pending": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#0c4a6e" : "#e1f5fe",
                  },
                  "& .cp-row-saved": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.background.paper
                        : "#f0f8f0",
                  },
                  "& .cp-row-default": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.background.paper
                        : "white",
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDeleteDialog
          open={deleteDialog.open}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Confirm Deletion"
          message={
            <>
              Are you sure you want to delete this course entry?
              {deleteDialog.rowToDelete && (
                <Box
                  sx={{
                    border:
                      theme.palette.mode === "dark"
                        ? `1px solid ${theme.palette.grey[600]}`
                        : "none",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Course:{" "}
                    {deleteDialog.rowToDelete.courseName || "Not specified"}
                  </Typography>
                  <Typography variant="body2">
                    Instructor Type:{" "}
                    {deleteDialog.rowToDelete.instructorType || "Not specified"}
                  </Typography>
                </Box>
              )}
            </>
          }
        />

        {/* Cancel Course Confirmation Dialog */}
        <Dialog
          open={cancelDialog.open}
          onClose={handleCancelCancel}
          aria-labelledby="cancel-dialog-title"
          aria-describedby="cancel-dialog-description"
        >
          <ModalDialogTitle
            id="cancel-dialog-title"
            onClose={handleCancelCancel}
          >
            Confirm Course Cancellation
          </ModalDialogTitle>
          <DialogContent>
            <DialogContentText id="cancel-dialog-description">
              Are you sure you want to request cancellation of this course? This
              will mark the course as "Course Cancel Requested" and notify the
              administrator for review.
              {cancelDialog.rowToCancel && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[800]
                        : "#fff3e0",
                    borderRadius: 1,
                    border:
                      theme.palette.mode === "dark"
                        ? `1px solid ${theme.palette.grey[600]}`
                        : `1px solid ${theme.palette.warning.main}`,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Course:{" "}
                    {cancelDialog.rowToCancel.courseName || "Not specified"}
                  </Typography>
                  <Typography variant="body2">
                    Instructor Type:{" "}
                    {cancelDialog.rowToCancel.instructorType || "Not specified"}
                  </Typography>
                </Box>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelCancel} color="primary">
              No, Keep Course
            </Button>
            <Button
              onClick={handleCancelConfirm}
              color="warning"
              variant="contained"
              autoFocus
            >
              Yes, Request Cancellation
            </Button>
          </DialogActions>
        </Dialog>

        {/* Form Lock Information Dialog */}
        <Dialog
          open={formLockDialog.open}
          onClose={handleCloseFormLockDialog}
          aria-labelledby="form-lock-dialog-title"
          aria-describedby="form-lock-dialog-description"
          maxWidth="sm"
          fullWidth
        >
          <ModalDialogTitle
            id="form-lock-dialog-title"
            onClose={handleCloseFormLockDialog}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? theme.palette.warning.dark
                  : "#fff3e0",
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.warning.contrastText
                  : "#ed6c02",
            }}
          >
            <LockIcon />
            Form Locked
          </ModalDialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <DialogContentText id="form-lock-dialog-description">
              This form has been locked by the administrator. You can view
              existing data and add new courses. For existing courses, click
              "Edit Course" to modify course details (except course name and
              number of sections).
            </DialogContentText>
            <DialogContentText sx={{ mt: 2 }}>
              Any changes you make to existing courses will be saved directly
              and logged for administrator visibility.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseFormLockDialog}
              color="primary"
              variant="contained"
              size="small"
            >
              Got it
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Term Selection Dialog - shows list of CcpTerms for user to pick */}
        <Dialog
          open={termDialog.open && !isHsDialog}
          onClose={() => setTermDialog({ open: false, rowId: null })}
          aria-labelledby="term-dialog-title"
          fullWidth
          maxWidth="sm"
        >
          <ModalDialogTitle
            id="term-dialog-title"
            onClose={() => setTermDialog({ open: false, rowId: null })}
          >
            {isHsDialog ? "Select an HS Term" : "Select a Term"}
          </ModalDialogTitle>
          <DialogContent dividers>
            {dialogLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CircularProgress size={20} />
                <Typography>Loading terms...</Typography>
              </Box>
            ) : termList && termList.length > 0 ? (
              <Grid container spacing={2}>
                {termList.map((term) => (
                  <Grid item xs={12} sm={6} key={term.id || term.TermName}>
                    <Box
                      role="button"
                      onClick={() => handleTermSelect(term)}
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        cursor: "pointer",
                        "&:hover": {
                          boxShadow: 3,
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? theme.palette.action.hover
                              : theme.palette.grey[50],
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold" }}
                      >
                        {term.TermName}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          Start: {formatDate(term.StartDate)}
                        </Typography>
                        <Typography variant="body2">
                          End: {formatDate(term.EndDate)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography>No terms available.</Typography>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default CoursePlanningForm;
